import type { FunctionComponent } from 'react';
import classNames from 'classnames';
import find from 'lodash/find';
import get from 'lodash/get';
import includes from 'lodash/includes';
import toLower from 'lodash/toLower';
import { COUNTRY_SELECTION, startCase } from '@fff-web/fff-utilities';
import * as styles from './AddressOption.module.scss';

const formatAddress = (value: string) => startCase(toLower(value));

type AddressData = {
  primaryLine: string;
  secondaryLine?: string;
  city: string;
  state?: string;
  zip: string;
  countryCode: string;
};

type AddressOptionProps = {
  title: string;
  data: AddressData;
  suggestedFields?: string[];
  isSelected: boolean;
  onClick: () => void;
};

const AddressOption: FunctionComponent<AddressOptionProps> = ({
  title,
  data,
  suggestedFields = [],
  isSelected,
  onClick,
}) => {
  const { primaryLine, secondaryLine, city, state, zip, countryCode } = data;
  const icon = isSelected ? 'fas fa-check-square' : 'far fa-square';
  const suggestedField = (field: string) => includes(suggestedFields, field);

  return (
    <button
      type="button"
      className={classNames(styles.container, { [styles.selected]: isSelected })}
      onClick={onClick}
    >
      <i className={icon} />
      <div className={styles.address}>
        <p className={styles.title}>{`${title}:`}</p>
        <p className={classNames({ [styles.suggested]: suggestedField('primaryLine') })}>
          {formatAddress(primaryLine)}
        </p>
        {secondaryLine && (
          <p className={classNames({ [styles.suggested]: suggestedField('secondaryLine') })}>
            {formatAddress(secondaryLine)}
          </p>
        )}
        <p>
          <span className={classNames({ [styles.suggested]: suggestedField('city') })}>
            {formatAddress(city)}
          </span>
          <span>, </span>
          {state && (
            <span className={classNames({ [styles.suggested]: suggestedField('state') })}>
              {`${state} `}
            </span>
          )}
          <span className={classNames({ [styles.suggested]: suggestedField('zip') })}>{zip}</span>
        </p>
        <p>{get(find(COUNTRY_SELECTION, ['code', countryCode]), 'name')}</p>
      </div>
    </button>
  );
};

export default AddressOption;
